import {
  generateId,
  generateNumbersInRangeWithDuplicates,
} from '@freelancer/datastore/testing/helpers';
import type { PartialBy } from '@freelancer/types';
import { DisplayBidRelevancyLabelApi } from 'api-typings/projects/projects';
import type {
  BidShowcaseItem,
  ProjectViewBid,
} from './project-view-bids.model';
import { RECOMMENDED_CUTOFF } from '../bids/bids.backend-model';
import type { Bid } from '../bids/bids.model';
import type { User } from '../users/users.model';

export interface GenerateProjectViewBidsOptions {
  readonly bids: readonly Bid[];
  readonly minScore?: number;
  readonly maxScore?: number;
  readonly showcases?: readonly BidShowcaseItem[];
  readonly bidderCountryCodes?: readonly string[];
  readonly areBiddersFreelancerVerified?: readonly boolean[];
  readonly areBiddersPreferredFreelancers?: readonly boolean[];
  readonly ratingValues?: readonly (number | undefined)[];
  readonly bidderReviewCounts?: readonly number[];
  readonly bidderJobCompletionRates?: readonly number[];
  readonly bidderOverallRatings?: readonly number[];
  readonly areBiddersOnline?: readonly boolean[];
  readonly scoresList?: readonly number[];
  readonly adjustedScoresList?: readonly number[];
  readonly bidRelevanceLabels?: readonly DisplayBidRelevancyLabelApi[];
  readonly languages?: readonly string[];
}

export function generateProjectViewBidObjects({
  bids,
  minScore = 0,
  maxScore = 1,
  showcases = [],
  bidderCountryCodes = [],
  areBiddersFreelancerVerified = [],
  areBiddersPreferredFreelancers = [],
  ratingValues = [],
  bidderReviewCounts = [],
  bidderJobCompletionRates = [],
  bidderOverallRatings = [],
  areBiddersOnline = [],
  scoresList = [], // overrides minScore and maxScore if provided
  adjustedScoresList = [],
  bidRelevanceLabels = [],
  languages = [],
}: GenerateProjectViewBidsOptions): readonly ProjectViewBid[] {
  if (bids.length !== scoresList.length && scoresList.length > 0) {
    throw new Error(
      `The number of bids (${bids.length}) must match the number of scores provided (${scoresList.length}).`,
    );
  }

  const scores =
    scoresList.length > 0
      ? scoresList
      : generateNumbersInRangeWithDuplicates(
          minScore,
          maxScore,
          bids.length,
          'bidScores',
        );

  const actualMaxScore = Math.max(...scores);

  /**
   * There could be more than one with the max score.
   * If so we arbitrarily pick the first.
   * The score must also be above RECOMMENDED_CUTOFF to be recommended.
   */
  const recommendedBidIndex = scores.findIndex(
    score => score === actualMaxScore && score > RECOMMENDED_CUTOFF,
  );

  return bids.map((bid, index) => {
    const ratingValue = ratingValues.at(index);
    const relevanceLabel =
      bidRelevanceLabels.at(index) ?? DisplayBidRelevancyLabelApi.A;
    const score = scores.at(index) ?? -Math.log10(bid.id);
    const adjustedScore = adjustedScoresList.at(index);
    return {
      ...bid,
      score,
      adjustedScore,
      recommended: index === recommendedBidIndex,
      isVideoBid: !!bid.pitchId,
      showcases,
      bidderCountryCode: bidderCountryCodes.at(index),
      isBidderFreelancerVerified: areBiddersFreelancerVerified.at(index),
      isBidderPreferredFreelancer: areBiddersPreferredFreelancers.at(index),
      ratingValue,
      bidderReviewCount: bidderReviewCounts.at(index) ?? 0,
      bidderJobCompletionRate: bidderJobCompletionRates.at(index) ?? 0,
      bidderOverallRating: bidderOverallRatings.at(index) ?? 2.5,
      isBidderOnline: areBiddersOnline.at(index),
      relevanceLabel,
      language: languages.at(index),
    };
  });
}

/**
 * Transformer for UI tests that need to propagate pushes from `bids` to
 * `projectViewBids`, e.g. FPVP bid form.
 */
export function bidToProjectViewBidTransformer(
  authUid: number,
  bid: PartialBy<Bid, 'id'>,
): ProjectViewBid {
  return {
    ...bid,
    id: bid.id ?? generateId(),
    score: bid.score ?? Number.NEGATIVE_INFINITY, // FPVP doesn't get to see bid scores
    recommended: false,
    isVideoBid: !!bid.pitchId,
    shortlisted: bid.shortlisted ?? undefined,
    showcases: [],
    relevanceLabel: DisplayBidRelevancyLabelApi.A,
  };
}

/**
 * Orders by recommended, then sponsored, then bid score descending, then
 * submit date descending. Used to assert the order of lists of bids in UI
 * tests.
 *
 * This comparator is deliberately standalone: it must NOT delegate to
 * `sortBidsByRank` (or any other production ordering). A UI test that computes
 * its expected order from the code under test asserts nothing, and the two
 * consumers do not even share an ordering - the bid list ranks by
 * `sortBidsByRank`, while the dashboard newsfeed sorts by score then submit
 * date on its own. Restating the rules here is the point.
 *
 * The submit date tie-break matches the newsfeed exactly. The bid list instead
 * leaves equally ranked bids in the order they arrived, so callers asserting
 * bid list order must seed bids in descending submit date - which `createBids`
 * does - for the two to agree.
 */
export function orderBidsByBidScore(
  bids: readonly ProjectViewBid[],
  bidders: readonly Pick<User, 'id' | 'displayName' | 'username'>[],
): readonly (ProjectViewBid & {
  readonly displayName: string | undefined;
  readonly username: string | undefined;
})[] {
  return bids
    .map(bid => ({
      ...bid,
      displayName: bidders.find(user => user.id === bid.bidderId)?.displayName,
      username: bidders.find(user => user.id === bid.bidderId)?.username,
    }))
    .sort((a, b) => {
      // `sponsored` is a bid upgrade fee, so 0 and undefined both mean "not sponsored"
      const aIsSponsored = a.sponsored !== undefined && a.sponsored !== 0;
      const bIsSponsored = b.sponsored !== undefined && b.sponsored !== 0;

      if (a.recommended !== b.recommended) {
        return a.recommended ? -1 : 1;
      }
      if (aIsSponsored !== bIsSponsored) {
        return aIsSponsored ? -1 : 1;
      }
      if (a.score === b.score) {
        return b.submitDate - a.submitDate;
      }
      return b.score - a.score;
    });
}
